import React, { useEffect, useState, memo } from 'react';
import PropTypes from 'prop-types';
import noScroll from 'no-scroll';
import cn from 'classnames';
import Portal from './Portal';
import { MODAL_ANIMATION_TIME } from '../../utils/helpers/constants';
import { PortalStyles } from './styled';

const ReactModal = ({
  children,
  isOpen = false,
  onAfterOpen,
  onRequestClose,
  overlayClassName,
  contentClassName,
  role = 'dialog',
  modalClassScope,
  modalId,
  animation,
  actionButton,
  isWrapperWithScroll = true,
  isTransparentOnOpen
}) => {
  if (typeof window === 'undefined') {
    return null;
  }
  const classScope = modalClassScope || 'modal';
  const banner = document.getElementById('js-lotriver-desk-top-banner');
  const modalClassNames = {
    portal: `${classScope}__portal`,
    bodyOpen: `${classScope}__body--open`,
    overlay: `${classScope}__overlay`,
    content: `${classScope}__content`
  };
  const [afterOpen, setAfterOpen] = useState(false);
  const [shouldClose, setShouldClose] = useState(null);
  const [noScrollingFlag, setNoScrollingFlag] = useState(false);
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);

  const hasAnimation = !!animation;

  const beforeOpen = () => {
    if (document.body.classList.contains(modalClassNames.bodyOpen)) {
      setNoScrollingFlag(true);
      return false;
    }

    document.body.classList.add(modalClassNames.bodyOpen);
    noScroll.on();
  };

  const afterClose = () => {
    if (noScrollingFlag) {
      setNoScrollingFlag(false);
      return false;
    }

    document.body.classList.remove(modalClassNames.bodyOpen);
    noScroll.off();
  };

  const openModal = () => {
    beforeOpen();
    setAfterOpen(true);
  };

  const requestClose = () => onRequestClose?.();

  const handleOverlayMouseDown = e => {
    setMouseDownOnOverlay(e.target === e.currentTarget);
  };

  const handleOverlayOnClick = () => {
    if (!mouseDownOnOverlay) return;
    if (shouldClose === null) {
      setShouldClose(true);
    }
  };

  const handleContentOnClick = event => {
    setMouseDownOnOverlay(false);
    setShouldClose(false);
    event.stopPropagation();
  };

  const portalClassNames = cn(modalClassNames.portal, {
    [`${modalClassNames.portal}--closed-${animation}`]: hasAnimation && !isOpen,
    [`${modalClassNames.portal}--opened-${animation}`]: isOpen && hasAnimation,
    [`${modalClassNames.portal}--transparent`]: isTransparentOnOpen
  });

  const buildClassNames = (which, additional, hasTopBanner = false) => {
    const classNames =
      typeof additional === 'object'
        ? additional
        : {
            base: modalClassNames[which],
            afterOpen: `${modalClassNames[which]}--after-open`
          };
    let className = classNames.base;

    if (onAfterOpen && afterOpen) {
      className = `${className} ${classNames.afterOpen}`;
    }

    if (isWrapperWithScroll) {
      className = `${className} ${modalClassNames[which]}--with-scroll`;
    }

    const updatedClassNames =
      typeof additional === 'string' && additional
        ? `${className} ${additional}`
        : className;

    return hasTopBanner
      ? `${updatedClassNames} modal__overlay--has-top-banner`
      : updatedClassNames;
  };

  useEffect(() => {
    if (isOpen) {
      openModal();
    }

    return () => {
      if (isOpen) afterClose();
    };
  }, [isOpen, noScrollingFlag]);

  useEffect(() => {
    if (afterOpen && onAfterOpen) {
      onAfterOpen();
    }
  }, [afterOpen]);

  useEffect(() => {
    if (shouldClose) {
      requestClose();
    }

    setShouldClose(null);
  }, [shouldClose]);

  useEffect(() => {
    if (isOpen || !hasAnimation) {
      setIsOpenInternal(isOpen);
    }
    if (!isOpen) {
      setTimeout(() => {
        setIsOpenInternal(false);
      }, MODAL_ANIMATION_TIME);
    }
  }, [isOpen, hasAnimation]);

  return (
    <Portal portalClassName={portalClassNames} id={modalId}>
      {(isOpen || isOpenInternal) && (
        <PortalStyles>
          <div
            className={buildClassNames(
              'overlay',
              overlayClassName,
              !!banner?.getBoundingClientRect().height
            )}
            onMouseDown={handleOverlayMouseDown}
            onClick={handleOverlayOnClick}
            onKeyPress={handleOverlayOnClick}
            role={role}
          >
            <div
              className={buildClassNames('content', contentClassName)}
              onClick={handleContentOnClick}
              onKeyPress={handleContentOnClick}
              role={role}
            >
              {children}
            </div>

            {actionButton}
          </div>
        </PortalStyles>
      )}
    </Portal>
  );
};

ReactModal.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  isOpen: PropTypes.bool.isRequired,
  onAfterOpen: PropTypes.func,
  onRequestClose: PropTypes.func,
  overlayClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  role: PropTypes.string,
  modalClassScope: PropTypes.string,
  modalId: PropTypes.string,
  animation: PropTypes.oneOf(['right', 'bottom', 'top']),
  actionButton: PropTypes.node,
  isWrapperWithScroll: PropTypes.bool,
  isTransparentOnOpen: PropTypes.bool
};

export default memo(ReactModal);
